import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '../api';

const BLUE = '#2B7FFF';

export default function Find() {
  const [pros, setPros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.get('/barbers/directory')
      .then(r => setPros(r.data))
      .finally(() => setLoading(false));
  }, []);

  const filtered = pros.filter(p =>
    !search ||
    p.displayName.toLowerCase().includes(search.toLowerCase()) ||
    p.location.toLowerCase().includes(search.toLowerCase()) ||
    p.title.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ minHeight: '100vh', background: 'transparent', color: '#fff', fontFamily: "'Inter',-apple-system,sans-serif" }}>
      <Helmet>
        <title>Find a Service Professional Near You | Mane</title>
        <meta name="description" content="Book with top independent service professionals near you. Real reviews, instant booking, no hassle." />
        <meta property="og:title" content="Find a Service Professional Near You | Mane" />
        <meta property="og:description" content="Book with top independent service professionals near you. Real reviews, instant booking, no hassle." />
        <meta property="og:locale" content="en_US" />
        <meta property="og:image" content="https://joinmane.com/og-image-v8.png" />
        <link rel="canonical" href="https://joinmane.com/find" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "Professionals on Mane",
          "description": "Independent service professionals available for online booking via Mane",
          "url": "https://joinmane.com/find",
        })}</script>
      </Helmet>

      {/* Header */}
      <div style={{ borderBottom: '1px solid rgba(43,127,255,0.12)', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(5,8,16,0.9)', backdropFilter: 'blur(16px)', position: 'sticky', top: 0, zIndex: 10 }}>
        <Link to="/" style={{ color: BLUE, fontWeight: 900, fontSize: '18px', letterSpacing: '0.2em', textDecoration: 'none', textTransform: 'uppercase' }}>MANE</Link>
        <Link to="/join" style={{ background: BLUE, color: '#fff', padding: '8px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: 700, textDecoration: 'none', letterSpacing: '0.04em' }}>Join as a Pro →</Link>
      </div>

      <div style={{ maxWidth: '760px', margin: '0 auto', padding: '48px 24px' }}>
        {/* Hero text */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div style={{ color: BLUE, fontSize: '11px', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: '12px' }}>Book a Pro</div>
          <h1 style={{ fontSize: 'clamp(32px,6vw,56px)', fontWeight: 900, letterSpacing: '-0.03em', lineHeight: 1.1, marginBottom: '16px' }}>Find Your Pro</h1>
          <p style={{ color: '#9CA3B4', fontSize: '15px', lineHeight: 1.7 }}>Independent professionals. Real reviews. Book in seconds.</p>
        </div>

        {/* Search */}
        <div style={{ marginBottom: '32px' }}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, city, or service..."
            style={{
              width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '10px', padding: '14px 18px', color: '#fff', fontSize: '15px',
              outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
            }}
          />
        </div>

        {/* Listings */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#7A8499' }}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#7A8499', fontSize: '15px' }}>
            {search ? 'No matches found.' : 'No pros listed yet — check back soon.'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {filtered.map(pro => (
              <Link
                key={pro.slug}
                to={`/${pro.slug}`}
                style={{ textDecoration: 'none', display: 'block', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '14px', padding: '24px', transition: 'border-color 0.2s', color: 'inherit' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(43,127,255,0.35)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: '17px', marginBottom: '4px' }}>{pro.displayName}</div>
                    <div style={{ color: '#9CA3B4', fontSize: '13px', marginBottom: '8px' }}>{pro.title} · {pro.location}</div>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                      {pro.reviewScore && (
                        <span style={{ fontSize: '12px', color: '#f5c518', fontWeight: 700 }}>
                          ★ {pro.reviewScore} {pro.reviewCount && <span style={{ color: '#7A8499', fontWeight: 400 }}>({pro.reviewCount})</span>}
                        </span>
                      )}
                      {pro.hoursText && <span style={{ fontSize: '11px', color: '#7A8499' }}>{pro.hoursText}</span>}
                    </div>
                  </div>
                  <span style={{ background: BLUE, color: '#fff', padding: '10px 22px', borderRadius: '8px', fontSize: '13px', fontWeight: 700, whiteSpace: 'nowrap', alignSelf: 'center' }}>
                    Book Now →
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* CTA for pros */}
        <div style={{ marginTop: '60px', textAlign: 'center', padding: '40px', background: 'rgba(43,127,255,0.04)', border: '1px solid rgba(43,127,255,0.15)', borderRadius: '16px' }}>
          <div style={{ fontSize: '20px', fontWeight: 800, marginBottom: '10px' }}>Are you a service professional?</div>
          <p style={{ color: '#9CA3B4', fontSize: '14px', marginBottom: '24px', lineHeight: 1.7 }}>Get your own booking page, client management, and AI tools. Starting at $29/mo.</p>
          <Link to="/join" style={{ background: BLUE, color: '#fff', padding: '14px 36px', borderRadius: '10px', fontSize: '14px', fontWeight: 800, textDecoration: 'none' }}>Get Your Page →</Link>
        </div>
      </div>
    </div>
  );
}
